/**
 * Expandable row in the data-driven Linked Accounts pane. Renders a provider
 * summary plus the user's linked accounts for that provider.
 */

import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown, Globe, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  providerLogoSizeClass,
  resolveProviderLogo,
} from "@/components/external-apis/provider-logos";
import { cn } from "@/lib/utils";
import type { LinkedAccount, MeConnection } from "@/services/me-connections-service";

interface ConnectionRowProps {
  conn: MeConnection;
  busy: boolean;
  onConnect: (conn: MeConnection) => void;
  onEditAccount: (conn: MeConnection, account: LinkedAccount) => void;
  onDisconnectAccount: (conn: MeConnection, account: LinkedAccount) => void;
}

export function ConnectionRow({
  conn,
  busy,
  onConnect,
  onEditAccount,
  onDisconnectAccount,
}: ConnectionRowProps) {
  const logoSrc = resolveProviderLogo(conn.logo);
  const linkedCount = conn.linkedAccounts.length;
  const hasLinkedAccounts = linkedCount > 0;
  const [expanded, setExpanded] = useState(hasLinkedAccounts);
  const spinner = busy ? <Loader2 className="mr-2 size-3 animate-spin" /> : null;

  useEffect(() => {
    if (hasLinkedAccounts) setExpanded(true);
  }, [hasLinkedAccounts]);

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex items-center gap-3 p-4">
        <button
          type="button"
          className={cn(
            "flex min-w-0 flex-1 items-center gap-3 text-left",
            hasLinkedAccounts ? "cursor-pointer" : "cursor-default",
          )}
          onClick={() => {
            if (hasLinkedAccounts) setExpanded((value) => !value);
          }}
          aria-expanded={hasLinkedAccounts ? expanded : undefined}
        >
          <ProviderMark logoSrc={logoSrc} logo={conn.logo} label={conn.displayName} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-base font-semibold text-foreground">
                {conn.displayName}
              </h3>
              {hasLinkedAccounts ? (
                <Badge
                  variant="outline"
                  className="h-6 rounded-md border-muted-foreground/20 bg-muted px-2 text-[11px] text-muted-foreground"
                >
                  {linkedCount} linked
                </Badge>
              ) : null}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {description(conn)}
            </p>
          </div>
          {hasLinkedAccounts ? (
            <ChevronDown
              className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform",
                expanded ? "rotate-180" : "",
              )}
            />
          ) : null}
        </button>

        <div className="shrink-0">{action()}</div>
      </div>

      {hasLinkedAccounts && expanded ? (
        <div className="border-t border-border bg-background">
          <div className="divide-y divide-border">
            {conn.linkedAccounts.map((account) => {
              const subtitle = accountSubtitle(account);

              return (
                <div
                  key={account.id}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                        {accountTitle(account)}
                      </span>
                      {subtitle ? (
                        <span className="min-w-0 truncate text-xs text-muted-foreground">
                          {subtitle}
                        </span>
                      ) : null}
                      {account.isDefault ? (
                        <Badge
                          variant="secondary"
                          className="h-5 shrink-0 bg-success px-1.5 text-[10px] text-success-foreground hover:bg-success"
                        >
                          Default
                        </Badge>
                      ) : null}
                      {account.environment ? (
                        <Badge
                          variant="outline"
                          className="h-5 shrink-0 px-1.5 text-[10px] capitalize"
                        >
                          {account.environment}
                        </Badge>
                      ) : null}
                    </div>
                    {account.description ? (
                      <p className="mt-1 truncate text-xs leading-5 text-muted-foreground">
                        {account.description}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      title="Edit account details"
                      onClick={() => onEditAccount(conn, account)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      title="Disconnect account"
                      onClick={() => onDisconnectAccount(conn, account)}
                      disabled={busy}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );

  function action() {
    if (conn.linkedAccounts.length > 0) {
      return (
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => onConnect(conn)}
          disabled={busy}
        >
          {spinner}
          {!busy ? <Plus className="size-3.5" /> : null}
          Connect another
        </Button>
      );
    }
    return (
      <Button size="sm" onClick={() => onConnect(conn)} disabled={busy}>
        {spinner}
        Connect
      </Button>
    );
  }
}

function accountTitle(account: LinkedAccount): string {
  return account.accountLabel || account.displayName || account.externalId;
}

function accountSubtitle(account: LinkedAccount): string | undefined {
  if (account.accountLabel && account.displayName !== account.accountLabel) {
    return account.displayName;
  }

  return undefined;
}

function ProviderMark({
  logoSrc,
  logo,
  label,
}: {
  logoSrc?: string;
  logo?: string;
  label: string;
}) {
  return (
    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
      {logoSrc ? (
        <img
          src={logoSrc}
          alt=""
          className={cn("h-6 w-6 object-contain", providerLogoSizeClass(logo))}
        />
      ) : (
        <span className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-base font-semibold uppercase text-primary">
          {label.charAt(0) || <Globe className="size-5" />}
        </span>
      )}
    </div>
  );
}

function description(conn: MeConnection): ReactNode {
  if (conn.linkedAccounts.length > 0) {
    return `${conn.linkedAccounts.length} account${
      conn.linkedAccounts.length === 1 ? "" : "s"
    } connected. Expand to review account details and agent guidance.`;
  }
  if (conn.status === "notConfigured") {
    return null;
  }
  return conn.purpose === "identity"
    ? "Link your account so agents recognize you across this channel."
    : "Connect your account so agents can act on it on your behalf.";
}
